import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { format } from 'date-fns';
import dayjs from 'dayjs';
ChartJS.register(...registerables);

function getGradient(ctx, chartArea) {
    let width, height, gradient;
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!gradient || width !== chartWidth || height !== chartHeight) {
        width = chartWidth;
        height = chartHeight;
        const x0 = chartArea.left;
        const y0 = 0;
        const x1 = chartArea.right;
        const y1 = 0;
        gradient = ctx.createLinearGradient(x0, y0, x1, y1);
        gradient.addColorStop(0, '#36F7C1'); // Green
        gradient.addColorStop(1, '#40C0EE'); // Blue
    }

    return gradient;
}

const StackedBarChart = React.memo(({ testCountData }) => {
    const [labels, setLabels] = useState([]);
    const [fetchedData, setFetchedData] = useState([]);
    const [isLoading, setIsLoading] = useState(!testCountData || testCountData.length === 0);

    useEffect(() => {
        if (!testCountData || testCountData.length === 0) {
            setIsLoading(true);
            return;
        }
        const labels = testCountData.map(item => item.date);
        const data = testCountData.map(item => ({
            x: item.date,
            y: item.count
        }));
        setLabels(labels);
        setFetchedData(data);
        setIsLoading(false);
    }, [testCountData]);

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
                position: 'top',
            },
            title: {
                display: false,
                text: 'Nombres de tests réalisés',
            },
            tooltip: {
                backgroundColor: 'black',
                displayColors: false,
                yAlign: 'top',
                position: 'nearest',
                callbacks: {
                    title: (tooltipItems) => {
                        return dayjs(tooltipItems[0].raw?.x).format('DD MMMM YYYY');
                    },
                    label: (tooltipItem) => {
                        return ` ${tooltipItem.raw?.y || tooltipItem.raw}`;
                    }
                },
                titleFont: {
                    size: 14,
                    weight: 'normal',
                    color: 'gray',
                    align: 'center',
                    family: 'Inter, sans-serif',
                },
                bodyFont: {
                    size: 16,
                    weight: 'bold',
                    color: 'white',
                    align: 'center',
                    family: 'Inter, sans-serif',
                }
            }
        },
        interaction: {
            intersect: false,
        },
        elements: {
            point: {
                backgroundColor: 'yellow',
                radius: 5,
                hoverBackgroundColor: 'yellow'
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'day',
                    parser: 'yyyy-MM-dd',
                    round: 'day',
                    unitStepSize: 7,
                    displayFormats: {
                        day: 'MMM d'
                    },
                },
                ticks: {
                    source: 'auto',
                    autoSkip: true,
                    maxTicksLimit: 8,
                    maxRotation: 0
                },
                grid: {
                    z: 0,
                },
                title: {
                    display: true
                }
            },
            y: {
                display: true,
                grid: {
                    display: false,
                },
                title: {
                    display: false,
                    text: 'Nombre'
                },
                suggestedMin: 0,
                suggestedMax: Math.max.apply(Math, fetchedData.map(data => data.y)) + 2,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    const data = {
        labels: labels,
        datasets: [
            {
                label: 'Tests réalisés',
                data: fetchedData,
                parsing: {
                    xAxisKey: 'x',
                    yAxisKey: 'y'
                },
                fill: true,
                backgroundColor: 'rgba(71, 134, 198, 1)',
                borderColor: 'rgba(71, 134, 230, 1)',
                pointBackgroundColor: 'rgba(135,206,250, 0)',
                tension: 0,
                backgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) return;
                    return getGradient(ctx, chartArea);
                },
                pointRadius: 0,
            }
        ],
    };

    if (isLoading) return <div>Loading...</div>;

    return <Line options={options} data={data} />;
}, (prevProps, nextProps) => {
    return JSON.stringify(prevProps.testCountData) === JSON.stringify(nextProps.testCountData);
});

export default StackedBarChart;